import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// emails_sync deliberately has no realtime publication (subject/body
// content is as sensitive as meeting transcripts — see migration 088's
// rationale). This hook polls instead of subscribing, and exposes
// `refetch` so callers can force an immediate recount right after marking
// an email read (better UX than waiting for the next poll tick).
const POLL_INTERVAL_MS = 60_000;

// Module-scoped shared store: every mounted useUnreadEmailCount() instance
// (Sidebar, SmartInbox, ...) subscribes here instead of holding its own
// independent count. This means a single refetch() call from any one
// instance (e.g. SmartInbox right after markEmailRead) immediately updates
// every other mounted instance (e.g. the Sidebar badge), instead of each
// instance polling on its own 60s cadence and drifting out of sync. Kept
// as a hand-rolled subscriber Set rather than pulling in a state library —
// this is a single number with no persistence/SSR needs.
let sharedCount = 0;
const subscribers = new Set<(count: number) => void>();

const notifySubscribers = (count: number) => {
  sharedCount = count;
  subscribers.forEach((cb) => cb(count));
};

const fetchUnreadCount = async (): Promise<void> => {
  // Only inbound mail counts toward the badge. `emails_sync.direction` (see
  // migration 095) has no NOT NULL/default — it's TEXT CHECK (direction IN
  // ('inbound','outbound')) with historical rows left at NULL until a sync
  // populates them. Treat NULL as inbound (the conservative, backward
  // -compatible reading: those rows predate direction-based distinction and
  // were never outbound). A plain .eq('direction', 'inbound') would silently
  // exclude every NULL row from PostgREST's evaluation, undercounting.
  const { count: unreadCount, error } = await supabase
    .from('emails_sync')
    .select('id', { count: 'exact', head: true })
    .eq('is_read', false)
    .or('direction.eq.inbound,direction.is.null');

  if (error) {
    console.error('Failed to load unread email count:', error);
    return;
  }
  notifySubscribers(unreadCount ?? 0);
};

export const useUnreadEmailCount = () => {
  const [count, setCount] = useState(() => sharedCount);

  const refetch = useCallback(async () => {
    await fetchUnreadCount();
  }, []);

  useEffect(() => {
    subscribers.add(setCount);
    // Sync in case the shared value changed between render and this effect
    // running (e.g. another instance's refetch landed in between).
    setCount(sharedCount);
    refetch();
    const interval = setInterval(refetch, POLL_INTERVAL_MS);
    return () => {
      subscribers.delete(setCount);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { count, refetch };
};

export const markEmailRead = async (
  emailId: string
): Promise<{ ok: true; updated: boolean } | { ok: false; error: string }> => {
  const { data, error } = await supabase
    .from('emails_sync')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', emailId)
    .eq('is_read', false)
    .select();

  if (error) return { ok: false, error: error.message };

  // Check if any rows were actually affected. Under RLS, a base 'user' role
  // lacks UPDATE permission (see migration 097 — only project_manager+ can
  // update). An RLS-denied UPDATE that matches zero rows returns { error: null, data: [] }
  // rather than an error. Without this check, the update silently fails and there's
  // no way to diagnose it. We log a console warning so it's visible in dev tools
  // rather than completely undiagnosable, but don't interrupt the user's reading flow
  // with a toast. `updated` lets callers gate optimistic local UI updates on whether
  // the write actually happened, instead of assuming success whenever no error was
  // thrown (an RLS-denied update should not be reflected as "read" in the UI).
  const updated = !!data && data.length > 0;
  if (!updated) {
    console.warn(
      `markEmailRead: Update matched 0 rows for emailId=${emailId}. ` +
      'This is likely due to missing RLS UPDATE permission for the current role (base users cannot update emails_sync — see migration 097).'
    );
  }

  return { ok: true, updated };
};
